<script setup lang="ts">
import type { createCalendarControlsPlugin } from '@schedule-x/calendar-controls';
import type { Temporal } from 'temporal-polyfill';
import type { ScheduleXViewId } from './LayoutCalendar.vue';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ScheduleXPlainDate } from './LayoutCalendar.vue';

const props = defineProps<{
	range: { start: string; end: string };
	calendarControls: ReturnType<typeof createCalendarControlsPlugin>;
}>();

const { t } = useI18n();

const currentView = defineModel<ScheduleXViewId>({ required: true });

const viewOptions: { label: string; value: ScheduleXViewId }[] = [
	{ label: t('layout.calendar.views.month'), value: 'month-grid' },
	{ label: t('layout.calendar.views.week'), value: 'week' },
	{ label: t('layout.calendar.views.day'), value: 'day' },
	{ label: t('layout.calendar.views.list'), value: 'list' },
];

function getSmallNavigationUnit() {
	switch (currentView.value) {
		case 'month-grid':
		case 'list':
			return t('layout.calendar.views.month').toLowerCase();
		case 'week':
			return t('layout.calendar.views.week').toLowerCase();
		case 'day':
			return t('layout.calendar.views.day').toLowerCase();
	}
}

function getLargeNavigationUnit() {
	switch (currentView.value) {
		case 'month-grid':
		case 'list':
			return t('layout.calendar.views.year').toLowerCase();
		case 'week':
		case 'day':
			return t('layout.calendar.views.month').toLowerCase();
	}
}

const prevLargeLabel = computed(() => t('layout.calendar.nav.previous', { unit: getLargeNavigationUnit() }));
const prevLabel = computed(() => t('layout.calendar.nav.previous', { unit: getSmallNavigationUnit() }));
const nextLabel = computed(() => t('layout.calendar.nav.next', { unit: getSmallNavigationUnit() }));
const nextLargeLabel = computed(() => t('layout.calendar.nav.next', { unit: getLargeNavigationUnit() }));

function toLocalDate(dateStr: string): Date {
	const [y, m, d] = dateStr.split('-').map(Number);
	return new Date(y!, m! - 1, d);
}

const headerTitle = computed(() => {
	const { start, end } = props.range;

	if (currentView.value === 'day') {
		return toLocalDate(start).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
	}

	if (currentView.value === 'week') {
		const s = toLocalDate(start);
		const e = toLocalDate(end);
		return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
	}

	const mid = new Date((toLocalDate(start).getTime() + toLocalDate(end).getTime()) / 2);
	return mid.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
});

// Some custom functions for the calendar layout
// We use these instead of the components from schedule-x to give it a more Monospace feeling
function getNavigationDelta(): Temporal.DurationLike {
	switch (currentView.value) {
		case 'month-grid':
		case 'list':
			return { months: 1 };
		case 'week':
			return { weeks: 1 };
		case 'day':
			return { days: 1 };
	}
}

function getLargeNavigationDelta(): Temporal.DurationLike {
	switch (currentView.value) {
		case 'month-grid':
		case 'list':
			return { years: 1 };
		case 'week':
		case 'day':
			return { months: 1 };
	}
}

function goToday() {
	props.calendarControls.setDate(ScheduleXPlainDate.from(new Date().toISOString().slice(0, 10)));
}

function goNext() {
	const date = props.calendarControls.getDate();
	props.calendarControls.setDate(date.add(getNavigationDelta()));
}

function goPrev() {
	const date = props.calendarControls.getDate();
	props.calendarControls.setDate(date.subtract(getNavigationDelta()));
}

function goNextLarge() {
	const date = props.calendarControls.getDate();
	props.calendarControls.setDate(date.add(getLargeNavigationDelta()));
}

function goPrevLarge() {
	const date = props.calendarControls.getDate();
	props.calendarControls.setDate(date.subtract(getLargeNavigationDelta()));
}

watch(currentView, (view) => {
	props.calendarControls.setView(view);
});
</script>

<template>
	<div class="@container relative flex items-center px-6 py-1.5">
		<div class="flex items-center justify-center gap-1">
			<DButton
				icon="lucide:chevrons-left"
				color="neutral"
				variant="ghost"
				square
				size="xs"
				:aria-label="prevLargeLabel"
				:title="prevLargeLabel"
				class="hidden @w-sm:flex"
				@click="goPrevLarge"
			/>
			<DButton icon="lucide:chevron-left" color="neutral" variant="ghost" square size="xs" :aria-label="prevLabel" :title="prevLabel" @click="goPrev" />
			<DButton icon="lucide:chevron-right" color="neutral" variant="ghost" square size="xs" :aria-label="nextLabel" :title="nextLabel" @click="goNext" />
			<DButton
				icon="lucide:chevrons-right"
				color="neutral"
				variant="ghost"
				square
				size="xs"
				:aria-label="nextLargeLabel"
				:title="nextLargeLabel"
				class="hidden @w-sm:flex"
				@click="goNextLarge"
			/>
		</div>

		<DButton color="neutral" variant="outline" size="sm" class="ml-2 hidden @w-sm:block" @click="goToday">
			{{ $t('layout.calendar.today') }}
		</DButton>

		<span class="pointer-events-none inset-0 flex items-center justify-center text-sm font-semibold sm:absolute">{{ headerTitle }}</span>
		<span class="flex-1" />

		<DSelect
			v-model="currentView"
			:items="viewOptions"
			size="sm"
			class="w-28"
		/>
	</div>
</template>
